import { TextArea } from '@root/components'
import { RadioItem } from '@components/OnboardingRadioList'
import { useBusinessContext } from '@root/context'
import { useChurnReasons } from '@root/query'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ChurnReasonRadioList, GoalText, StepSection } from '../CancelFlow.styled'
import { CancelFlowFormValues, churnReasonSelectionNeedsText } from '../types'

export const ChurnReasonStep: React.FC = () => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const { data: churnReasons } = useChurnReasons(slug)
  const {
    control,
    register,
    formState: { errors }
  } = useFormContext<CancelFlowFormValues>()

  const selectedReasons = useWatch({ control, name: 'churnReasons' }) ?? []
  const reasonNeedsText = churnReasonSelectionNeedsText(selectedReasons, churnReasons)

  return (
    <StepSection>
      <GoalText>{t('settings.billing.churnReason.description')}</GoalText>

      <Controller
        control={control}
        name="churnReasons"
        render={({ field: { value, onChange } }) => (
          <ChurnReasonRadioList
            role="group"
            aria-label={t('settings.billing.churnReason.description')}
          >
            {(churnReasons ?? []).map((option, index) => (
              <RadioItem
                key={option.value}
                label={option.label}
                index={index}
                selected={value.includes(option.value)}
                value={option.value}
                onSelect={selectedValue => {
                  if (value.includes(selectedValue)) {
                    onChange(value.filter(reason => reason !== selectedValue))
                    return
                  }
                  onChange([...value, selectedValue])
                }}
              />
            ))}
          </ChurnReasonRadioList>
        )}
      />

      {reasonNeedsText && (
        <TextArea
          {...register('churnReasonOther', {
            validate: value => value.trim().length > 0 || t('validation.required')
          })}
          errors={errors.churnReasonOther}
          label={t('settings.billing.churnReason.detailsLabel')}
          placeholder={t('settings.billing.churnReason.detailsPlaceholder')}
          required
          rows={3}
          aria-required
        />
      )}
    </StepSection>
  )
}
